import LoadingSkeletonUntyped from "@shared/LoadingSkeleton";
import { captureException } from "@sentry/react";
import { getApiUrl } from "@utils/baseUrl";
import { ERROR_MSG } from "@utils/enum";
import { getIntelUrl } from "@utils/intelUrl";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { toast } from "react-toastify";
import CompanyResultsPanelUntyped from "../../components/companyResultsPanel";
import { companySearchTabs, getDefaultDateRange, initialMetrics } from "../../enum";
import { trackBulkSearch } from "../analytics";
import { downloadSelectedCompanies, getApiErrorMessage, getConsolidatedCompanies } from "../services";
import { applyConsolidatedSearch } from "../utils";
import type { ConsolidatedCompany } from "../types";

const LoadingSkeleton = LoadingSkeletonUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

const CompanyResultsPanel = CompanyResultsPanelUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

type ConfirmedCompaniesViewProps = {
  searchId: string;
  currentUserPlan?: string;
};

const ConfirmedCompaniesView = ({ searchId, currentUserPlan = "" }: ConfirmedCompaniesViewProps) => {
  const [tableData, setTableData] = useState<ConsolidatedCompany[]>([]);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<ConsolidatedCompany[]>([]);
  const [selectedRowData, setSelectedRowData] = useState<ConsolidatedCompany | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [countryFilterOptions, setCountryFilterOptions] = useState<{ country_code: string; key: string }[]>([]);

  const initialFilters = useMemo(() => ({ dateRange: getDefaultDateRange(currentUserPlan), listedCompany: null, jurisdiction: [], metrics: initialMetrics }), [currentUserPlan]);
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        const data = await getConsolidatedCompanies(searchId);
        if (!isActive) return;
        const companies = data?.companies || [];
        setTableData(companies);
        setTotalDataCount(data?.totalCompaniesCount || 0);
        const countriesOpt: Record<string, string> = {};
        companies.forEach((company) => {
          if (company.country_code) countriesOpt[company.country_code] = company.country ?? "";
        });
        setCountryFilterOptions(
          Object.keys(countriesOpt)
            .map((code) => ({ country_code: code, key: countriesOpt[code] }))
            .sort((a, b) => a.key.localeCompare(b.key))
        );
      } catch (error) {
        captureException(error);
        toast.error(getApiErrorMessage(error, ERROR_MSG));
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();
    return () => {
      isActive = false;
    };
  }, [searchId]);

  useEffect(() => {
    setSelectedRows([]);
  }, [filters]);

  const filteredTableData = useMemo(() => {
    const countryFilter = filters?.jurisdiction || [];
    let filteredData = applyConsolidatedSearch(tableData, searchQuery);

    if (countryFilter.length > 0) {
      filteredData = filteredData.filter((data) => countryFilter.includes(data.country));
    }

    if (filters.listedCompany) {
      filteredData = filteredData.filter((data) => {
        const hasTickers = Object.keys(data.stockTickerDetails || {}).length;
        return filters.listedCompany.key === "exclude" ? hasTickers === 0 : hasTickers > 0;
      });
    }

    return filteredData;
  }, [tableData, filters, searchQuery]);

  const getSlugHandler = async (companyId: string, suffix = "") => {
    try {
      const { data } = await axios.get(getApiUrl("companyProfiles/getSlug"), { params: { companyId } });
      if (data?.status === "success" && data?.data?.companySlug) {
        window.open(`${getIntelUrl()}${data.data.companySlug}/${data.data.aliasCompanyId}?country=multicountry&tab=I${suffix}`, "_blank");
      } else {
        toast.error("No company profile found in our system !!!");
      }
    } catch (error) {
      captureException(error);
      toast.error(getApiErrorMessage(error, ERROR_MSG));
    }
  };

  const handleDownloadSelected = async (companies: ConsolidatedCompany[]) => {
    await downloadSelectedCompanies(searchId, companies);
    trackBulkSearch("companyBulkSearchResultsDownloaded", { search_id: searchId, companies_count: companies.length });
  };

  // Not MatchResultsSkeleton: its Confidence/Review headers belong to the
  // review table and made a confirmed search look like it was back on review.
  if (isLoading)
    return (
      <div className="tw-mx-auto tw-max-w-[1336px] tw-px-5">
        <LoadingSkeleton lines={10} lineHeight={"40px"} width={"100%"} shape="rectangle" widthArr={["100%", "100%", "100%", "100%"]} />
      </div>
    );

  const searchSlot = (
    <div className="tw-flex tw-items-center tw-gap-3">
      <div className="tw-relative">
        <span className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-2.5 tw-flex tw-items-center tw-text-gray-400">
          <span className="material-symbols-outlined tw-text-[18px] tw-leading-none">search</span>
        </span>
        <input type="text" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search below list of companies by name or LEI" aria-label="Search below list of companies by name or LEI" className="tw-h-8 tw-w-96 tw-rounded-lg tw-border tw-border-gray-300 tw-bg-white tw-py-1.5 tw-pl-9 tw-pr-8 tw-text-sm tw-text-gray-900 placeholder:tw-text-gray-400 focus:tw-border-primary-purple-600 focus:tw-outline-none" />
        {searchQuery && (
          <button type="button" aria-label="Clear search" onClick={() => setSearchQuery("")} className="tw-absolute tw-inset-y-0 tw-right-2 tw-flex tw-items-center tw-border-none tw-bg-transparent tw-p-0 tw-leading-none tw-text-gray-400 hover:tw-text-gray-600">
            <span className="material-symbols-outlined tw-text-[18px] tw-leading-none">close</span>
          </button>
        )}
      </div>
    </div>
  );

  return <CompanyResultsPanel searchSlot={searchSlot} onDownloadOverride={handleDownloadSelected} tableData={filteredTableData} rawTableData={tableData} totalDataCount={totalDataCount} showSort={false} selectedRows={selectedRows} setSelectedRows={setSelectedRows} selectedRowData={selectedRowData} setSelectedRowData={setSelectedRowData} clearSelections={() => setSelectedRows([])} filters={filters} setFilters={setFilters} initialFilters={initialFilters} hasActiveFilters={!!filters.listedCompany || filters.jurisdiction.length > 0} countryFilterOptions={countryFilterOptions} currentUserPlan={currentUserPlan} searchedState={null} activeTab={companySearchTabs.COMPANY} getSlugHandler={getSlugHandler} source="company_bulk_search" />;
};

export default ConfirmedCompaniesView;
